//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  ILike2BuyV2InstagramDefaultV1Config,
  Like2BuyV2TemplateConfig,
} from '@apis/like2buy-v2/like2BuyV2StoreModel';
import { IInstagramLike2BuyV2Customizations } from '@instagram-account/like2buy-v2/constants/like2BuyV2InstagramConstants';
import { ILike2BuyCustomizations } from '@like2buy-v2/like2BuyV2UIUtils';
import FileId from '@models/FileId';

export function instagramStoreConfigToCustomizations(
  config: ILike2BuyV2InstagramDefaultV1Config,
): IInstagramLike2BuyV2Customizations {
  const customStrings = config.customStringsByLocale['en-us'];

  return {
    banner: {
      altText: {
        text: customStrings?.gallery?.header?.bannerSection?.bannerAlt ?? '',
      },
      desktopAltText: {
        text:
          customStrings?.gallery?.header?.bannerSection?.desktopBannerAlt ?? '',
      },
      crawlId: config.galleryBannerSection?.crawlId,
      destinationUrl: {
        text:
          customStrings?.gallery?.header?.bannerSection?.destinationUrl ?? '',
      },
      imageUpload: {
        imageFileName: config.galleryBannerSection?.imageFileName,
        imageFileId: config.galleryBannerSection?.imageFileId
          ? FileId(config.galleryBannerSection.imageFileId)
          : undefined,
      },
      desktopImageUpload: {
        desktopImageFileName: config.galleryBannerSection?.desktopImageFileId,
        desktopImageFileId: config.galleryBannerSection?.desktopImageFileId
          ? FileId(config.galleryBannerSection.desktopImageFileId)
          : undefined,
      },
      isBannerEnabled: config.galleryBannerSection?.isEnabled ?? false,
    },

    ctas: {
      galleryHeader: {
        text: customStrings?.gallery?.ctaBanner ?? '',
      },
      desktopGalleryHeader: {
        text: customStrings?.gallery?.desktopCtaBanner ?? '',
      },
      galleryMediaHover: {
        text: customStrings?.gallery?.item?.moreInfo ?? '',
      },
      productsContainer: {
        text: customStrings?.items?.products?.header ?? '',
      },
    },

    emailCapture: {
      cta: {
        text: customStrings?.gallery?.emailCapture?.cta ?? '',
      },
      isEnabled: config.galleryEmailCapture?.isEnabled ?? false,
      shouldRequireTermsAndConditions:
        config.galleryEmailCapture?.shouldRequireTermsAndConditions ?? false,
      termsAndConditionsUrl: {
        text: customStrings?.gallery?.emailCapture?.termsAndConditionsUrl ?? '',
      },
    },

    gallerySquareThumbnails: {
      isEnabled: config.gallerySquareThumbnails?.isEnabled ?? false,
    },
    gallerySkipDetailsForSingleProduct: {
      isEnabled: config.gallerySkipDetailsForSingleProduct?.isEnabled ?? false,
    },
  };
}

export function instagramCustomizationsToStoreConfig(
  existingStoreConfig: ILike2BuyV2InstagramDefaultV1Config,
  customizations: IInstagramLike2BuyV2Customizations,
): ILike2BuyV2InstagramDefaultV1Config {
  const {
    banner: {
      altText,
      desktopAltText,
      crawlId,
      destinationUrl,
      imageUpload: { imageFileName, imageFileId },
      desktopImageUpload: { desktopImageFileName, desktopImageFileId },
      isBannerEnabled: isBannerEnabled,
    },
    ctas: {
      galleryHeader,
      desktopGalleryHeader,
      galleryMediaHover,
      productsContainer,
    },
    emailCapture: {
      cta: emailCaptureCta,
      isEnabled: isEmailCaptureEnabled,
      shouldRequireTermsAndConditions,
      termsAndConditionsUrl,
    },
    gallerySquareThumbnails: { isEnabled: isGallerySquareThumbnailsEnabled },
    gallerySkipDetailsForSingleProduct: {
      isEnabled: isGallerySkipDetailsForSingleProductEnabled,
    },
  } = customizations;

  return {
    ...existingStoreConfig,

    galleryBannerSection: {
      crawlId: crawlId,
      imageFileId: imageFileId,
      imageFileName: imageFileName,
      desktopImageFileId: desktopImageFileId,
      desktopImageFileName: desktopImageFileName,
      isEnabled: isBannerEnabled,
    },

    gallerySquareThumbnails: {
      isEnabled: isGallerySquareThumbnailsEnabled,
    },

    gallerySkipDetailsForSingleProduct: {
      isEnabled: isGallerySkipDetailsForSingleProductEnabled,
    },

    galleryEmailCapture: {
      isEnabled: isEmailCaptureEnabled,
      shouldRequireTermsAndConditions,
    },

    customStringsByLocale: {
      ...existingStoreConfig.customStringsByLocale,
      'en-us': {
        ...existingStoreConfig.customStringsByLocale['en-us'],

        gallery: {
          ...existingStoreConfig.customStringsByLocale['en-us']?.gallery,

          header: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
              ?.header,
            bannerSection: {
              ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
                ?.header?.bannerSection,
              bannerAlt: altText.text || undefined,
              desktopBannerAlt: desktopAltText.text || undefined,
              destinationUrl: destinationUrl.text || undefined,
            },
          },

          emailCapture: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
              ?.emailCapture,
            cta: emailCaptureCta.text || undefined,
            termsAndConditionsUrl: termsAndConditionsUrl.text || undefined,
          },

          ctaBanner: galleryHeader.text || undefined,
          desktopCtaBanner: desktopGalleryHeader.text || undefined,

          item: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.gallery
              ?.item,
            moreInfo: galleryMediaHover.text || undefined,
          },
        },

        items: {
          ...existingStoreConfig.customStringsByLocale['en-us']?.items,

          products: {
            ...existingStoreConfig.customStringsByLocale['en-us']?.items
              ?.products,
            header: productsContainer.text || undefined,
          },
        },
      },
    },
  };
}

export function isInstagramLike2BuyV2Template(
  template: Like2BuyV2TemplateConfig,
): template is ILike2BuyV2InstagramDefaultV1Config {
  return template['@type'] === 'v1:instagram-default';
}

export function isInstagramLike2BuyV2Customizations(
  template: Like2BuyV2TemplateConfig,
  customizations: ILike2BuyCustomizations,
): customizations is IInstagramLike2BuyV2Customizations {
  return template['@type'] === 'v1:instagram-default';
}
